// assets/strap-builder.js
// Bespoke strap builder: drives the real variant options, mirrors every choice into
// line item properties, and animates the SVG preview. Deliberately dependency-free
// so a theme update cannot break it.

const MONO_ALLOWED = /[^A-Za-z0-9 .&'-]/g;
const MONO_MAX_LENGTH = 4;
/** The strap band is ~96 user units tall, so the stamp stays well under that. */
const MONO_MAX_FONT_SIZE = 30;
/** Widest the stamp may be, in SVG user units, to stay inside the short side. */
const MONO_MAX_WIDTH = 106;

class StrapBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.sbReady === 'true') return;
    this.dataset.sbReady = 'true';

    const source = this.querySelector('[data-sb-variants]');
    if (!source) return;

    try {
      this.variants = JSON.parse(source.textContent);
    } catch {
      return;
    }
    if (!Array.isArray(this.variants) || this.variants.length === 0) return;

    this.formId = this.querySelector('.sb')?.dataset.formId || '';
    this.scene = this.querySelector('[data-sb-scene]');
    this.priceEl = this.querySelector('[data-sb-price]');
    this.summaryEl = this.querySelector('[data-sb-summary]');
    this.buildCodeEl = this.querySelector('[data-sb-build-code]');
    this.monoField = this.querySelector('[data-sb-mono-field]');
    this.monoInput = this.querySelector('[data-sb-mono-input]');
    this.monoCount = this.querySelector('[data-sb-mono-count]');
    this.stageNote = this.querySelector('[data-sb-stage-note]');
    this.monoGroups = this.querySelectorAll('[data-sb-mono-group]');
    this.hasAutoFlipped = false;

    this.addEventListener('change', (event) => this.#handleChange(event));
    this.addEventListener('click', (event) => this.#handleClick(event));
    this.monoInput?.addEventListener('input', () => this.#handleMonoInput());
    this.#watchOptionalFields();

    this.#renderDeltas();
    this.#syncAll({ animate: false });
  }

  /**
   * Blank optional fields would otherwise reach the order as empty line item
   * properties. Dropping the name attribute keeps them out of the submission
   * while leaving the field editable (unlike `disabled`).
   */
  #watchOptionalFields() {
    this.querySelectorAll('[data-sb-optional]').forEach((field) => {
      const name = field.getAttribute('name');
      if (!name) return;

      const sync = () => {
        field.setAttribute('name', field.value.trim() ? name : '');
      };
      field.addEventListener('input', sync);
      sync();
    });
  }

  get form() {
    return this.formId ? document.getElementById(this.formId) : null;
  }

  get selectedOptionInputs() {
    return Array.from(this.querySelectorAll('[data-sb-option]:checked'));
  }

  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.dataset.sbRole === 'mono') {
      this.#setMonogramMode(target.dataset.mono === 'on');
    }

    const animateStitch = target.dataset.sbProp === 'stitch';
    this.#syncAll({ animate: true, animateStitch });
  }

  #handleClick(event) {
    const button = event.target.closest?.('[data-sb-view]');
    if (!button) return;
    this.#setFace(button.dataset.sbView);
  }

  #handleMonoInput() {
    const cleaned = this.monoInput.value.replace(MONO_ALLOWED, '').toUpperCase().slice(0, MONO_MAX_LENGTH);
    if (cleaned !== this.monoInput.value) this.monoInput.value = cleaned;
    if (this.monoCount) this.monoCount.textContent = `${cleaned.length}/${MONO_MAX_LENGTH}`;

    this.#paintMonogram(cleaned);
    this.#renderSummary();

    if (cleaned.length > 0 && !this.hasAutoFlipped) {
      this.hasAutoFlipped = true;
      this.#setFace('back');
    }
  }

  #setMonogramMode(on) {
    if (!this.monoField || !this.monoInput) return;

    this.monoField.hidden = !on;
    this.monoInput.disabled = !on;
    this.monoInput.required = on;

    if (on) {
      this.#paintMonogram(this.monoInput.value);
      if (!this.hasAutoFlipped) {
        this.hasAutoFlipped = true;
        this.#setFace('back');
      }
      requestAnimationFrame(() => this.monoInput.focus({ preventScroll: true }));
    } else {
      this.#paintMonogram('');
      this.#setFace('front');
    }
  }

  #paintMonogram(text) {
    const value = (text || '').trim();

    this.querySelectorAll('[data-sb-mono-main], [data-sb-mono-shadow]').forEach((node) => {
      node.textContent = value;
      node.style.fontSize = `${MONO_MAX_FONT_SIZE}px`;
    });

    // Measure rather than estimate: fonts differ, and the stamp must never
    // spill past the tail. MONO_MAX_WIDTH is in SVG user units.
    if (value.length > 0) {
      this.querySelectorAll('[data-sb-mono-main], [data-sb-mono-shadow]').forEach((node) => {
        let width = 0;
        try {
          width = node.getBBox().width;
        } catch {
          return;
        }
        if (width > MONO_MAX_WIDTH) {
          const scaled = Math.floor(MONO_MAX_FONT_SIZE * (MONO_MAX_WIDTH / width));
          node.style.fontSize = `${scaled}px`;
        }
      });
    }

    this.monoGroups.forEach((group) => {
      group.classList.toggle('is-shown', value.length > 0);
      if (value.length === 0) return;
      group.classList.remove('is-stamping');
      void group.getBoundingClientRect();
      group.classList.add('is-stamping');
    });
  }

  #setFace(face) {
    const isBack = face === 'back';
    this.scene?.classList.toggle('is-flipped', isBack);
    this.querySelectorAll('[data-sb-view]').forEach((button) => {
      const active = button.dataset.sbView === face;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  #matchVariant() {
    const chosen = [];
    this.selectedOptionInputs.forEach((input) => {
      const position = Number(input.dataset.sbOption);
      if (Number.isFinite(position)) chosen[position - 1] = input.value;
    });

    return this.variants.find((variant) =>
      chosen.every((value, index) => value === undefined || variant.options[index] === value)
    );
  }

  #syncAll({ animate = true, animateStitch = false } = {}) {
    const variant = this.#matchVariant();
    if (variant) this.currentVariant = variant;

    if (variant) {
      const idInput = this.form?.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = String(variant.id);
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.priceEl) this.priceEl.textContent = variant.priceText;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url);
      } catch {
        /* non-fatal */
      }
    }

    this.#paintPreview({ animate, animateStitch });
    this.#renderSummary(variant);
  }

  #prop(name) {
    return this.querySelector(`[data-sb-prop="${name}"]:checked`);
  }

  #role(name) {
    return this.querySelector(`[data-sb-role="${name}"]:checked`);
  }

  /**
   * "Tonal" thread and "Match leather" edge paint are not fixed colours — they are
   * whatever the chosen leather is. Repaint their swatch, their preview colour and,
   * critically, the submitted value so the order records what was actually sewn
   * ("Tonal (Cognac)") rather than an ambiguous "Tonal".
   * @param {HTMLInputElement | null} leather
   */
  #applyLeatherFollowers(leather) {
    if (!leather) return;

    const leatherName = leather.dataset.colour || leather.value.split('—').pop().trim();

    this.querySelectorAll('[data-follow-leather]').forEach((input) => {
      const isThread = input.dataset.sbProp === 'stitch';
      const colour = isThread ? leather.dataset.face : leather.dataset.edge || leather.dataset.face;

      input.dataset.face = colour;

      const swatch = input.closest('.sb__swatch');
      if (swatch) {
        swatch.style.setProperty('--sw', colour);
        swatch.style.setProperty('--sw-edge', colour);
      }

      const base = input.dataset.baseName || input.value.replace(/\s*\(.*\)$/, '');
      input.value = `${base} (${leatherName})`;

      if (input.checked) {
        this.style.setProperty(isThread ? '--sb-stitch' : '--sb-edge', colour);
      }
    });
  }

  #paintPreview({ animate, animateStitch }) {
    const leather = this.#prop('leather');
    const stitch = this.#prop('stitch');
    const lining = this.#prop('lining');
    const edge = this.#prop('edge');
    const length = this.#prop('length');
    const width = this.#role('width');
    const clasp = this.#role('clasp');
    const style = this.#role('stitchstyle');

    if (leather) {
      this.style.setProperty('--sb-leather', leather.dataset.face);
      this.style.setProperty('--sb-leather-edge', leather.dataset.edge || leather.dataset.face);
    }
    if (stitch) this.style.setProperty('--sb-stitch', stitch.dataset.face);
    if (lining) {
      this.style.setProperty('--sb-lining', lining.dataset.face);
      this.style.setProperty('--sb-lining-edge', lining.dataset.edge || lining.dataset.face);
    }

    this.#applyLeatherFollowers(leather);

    if (edge) {
      this.style.setProperty('--sb-edge', edge.dataset.face);
    }

    if (style) {
      const key = style.dataset.style;
      this.querySelectorAll('[data-sb-stitch-style]').forEach((node) => {
        node.classList.toggle('is-shown', node.dataset.sbStitchStyle === key);
      });

      // Thread colour only reaches the keeper on a stitchless strap.
      const stitchless = key === 'none';
      this.querySelectorAll('[data-sb-thread-row]').forEach((row) => {
        row.classList.toggle('is-muted', stitchless);
      });
      this.querySelectorAll('[data-sb-thread-note]').forEach((note) => {
        note.hidden = !stitchless;
      });
    }

    if (width) {
      const mm = parseFloat(width.value) || 20;
      const scale = Math.min(1.32, Math.max(0.82, mm / 20));
      this.querySelectorAll('[data-sb-strap]').forEach((node) => {
        node.style.transform = `scaleY(${scale.toFixed(3)})`;
      });
      this.querySelectorAll('[data-sb-dim-label]').forEach((node) => {
        node.textContent = width.value;
      });
    }

    if (length) {
      const scale = parseFloat(length.dataset.scale) || 1;
      this.querySelectorAll('[data-sb-tail]').forEach((node) => {
        node.style.transform = `scaleX(${scale})`;
      });
    }

    if (clasp) {
      const kind = clasp.dataset.clasp;
      this.querySelectorAll('[data-sb-hardware]').forEach((node) => {
        node.classList.toggle('is-shown', node.dataset.sbHardware === kind);
      });
    }

    if (animate && animateStitch) {
      this.querySelectorAll('[data-sb-stitch]').forEach((node) => {
        node.classList.remove('is-restitching');
        void node.getBoundingClientRect();
        node.classList.add('is-restitching');
      });
    }

    if (this.stageNote) {
      const parts = [];
      if (width) parts.push(width.value);
      if (length) parts.push(`${length.value} length`);
      if (clasp) parts.push(clasp.value.toLowerCase());
      this.stageNote.textContent = parts.join(' · ');
    }
  }

  #renderSummary(variant = this.currentVariant) {
    const rows = [];
    const leather = this.#prop('leather');
    const stitch = this.#prop('stitch');
    const lining = this.#prop('lining');
    const edge = this.#prop('edge');
    const length = this.#prop('length');
    const width = this.#role('width');
    const clasp = this.#role('clasp');
    const style = this.#role('stitchstyle');
    const mono = this.#role('mono');

    if (leather) rows.push(['Leather', leather.value]);
    if (style) rows.push(['Stitch style', style.value]);
    if (stitch) rows.push(['Thread', stitch.value]);
    if (edge) rows.push(['Edge paint', edge.value]);
    if (lining) rows.push(['Lining', lining.value]);
    if (length) rows.push(['Length', length.value]);
    if (width) rows.push(['Lug width', width.value]);
    if (clasp) rows.push(['Clasp', clasp.value]);

    const monoOn = mono?.dataset.mono === 'on';
    const monoText = monoOn ? (this.monoInput?.value || '').trim() : '';
    if (monoOn) rows.push(['Monogram', monoText || '—']);

    if (this.summaryEl) {
      this.summaryEl.replaceChildren(
        ...rows.flatMap(([label, value]) => {
          const dt = document.createElement('dt');
          dt.textContent = label;
          const dd = document.createElement('dd');
          dd.textContent = value;
          return [dt, dd];
        })
      );
    }

    if (this.buildCodeEl) {
      const readable = rows.map(([label, value]) => `${label}: ${value}`).join(' | ');
      this.buildCodeEl.value = variant?.sku ? `${variant.sku} — ${readable}` : readable;
    }
  }

  #renderDeltas() {
    const prices = this.variants.map((variant) => parseFloat(variant.price));
    const base = Math.min(...prices);
    const sample = this.variants.find((variant) => parseFloat(variant.price) === base)?.priceText || '';

    this.querySelectorAll('[data-sb-delta]').forEach((node) => {
      const value = node.dataset.sbDelta;
      const matching = this.variants.filter((variant) => variant.options.includes(value));
      if (matching.length === 0) return;

      const cheapest = Math.min(...matching.map((variant) => parseFloat(variant.price)));
      const delta = cheapest - base;
      node.textContent = delta > 0 ? `+ ${formatLike(sample, delta)}` : 'Included';
    });
  }
}

/**
 * Formats a cents amount using the shape of an already money-formatted string, so
 * we inherit the shop's currency symbol, separators and placement without guessing.
 * @param {string} sample - e.g. "$149.00", "Rs. 10,500.00", "2.000,00 €"
 * @param {number} cents - Liquid money values are integers in cents.
 */
function formatLike(sample, cents) {
  const amount = cents / 100;
  // Must end on a digit so a trailing space before the currency symbol survives.
  const match = sample.match(/\d[\d.,\s]*\d|\d/);
  if (!match) return amount.toFixed(2);

  const numeric = match[0].trim();
  const decimalComma = /,\d{1,2}$/.test(numeric);

  let formatted;
  try {
    formatted = new Intl.NumberFormat(decimalComma ? 'de-DE' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    formatted = amount.toFixed(2);
  }

  return sample.replace(match[0], formatted);
}

if (!customElements.get('strap-builder-component')) {
  customElements.define('strap-builder-component', StrapBuilder);
}
